import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { ComposeEmailModal } from './components/ComposeEmailModal';
import { fetchDispatchAccount } from '../../utils/mailDispatchApi';
import { fetchPdfBytes } from '../../utils/pdfDocumentBytes';
import { contactDisplayName, leadOutboundEmail, type Lead } from '../../types';
import { resolveDocumentBilling } from '../leads/utils/invoiceDebtor';
import {
  buildDocumentMailDraft,
  mailPurposeForDocumentTextType,
  type DocumentTextType,
} from '../leads/utils/documentMailDraft';
import type { SendPdfEmailTemplateValues } from '../leads/utils/sendPdfEmailBodyTemplate';

export type SharePdfByMailOptions = {
  /** PDF-URL (same-origin mit Cookies, sonst ohne) oder bereits geladenes `blob`. */
  url?: string;
  blob?: Blob;
  filename: string;
  /** Verknüpft die gesendete Mail mit dem Projekt (erscheint im Verlauf). */
  leadId?: string | number | null;
  /** Belegart für die Textvorlage; ohne Belegart neutraler Text. */
  documentTextType?: DocumentTextType | null;
  templateValues?: SendPdfEmailTemplateValues;
  to?: string;
  cc?: string;
  /** false: Empfänger nicht aus dem Projekt vorbelegen (z. B. Eingangsrechnungen an die Buchhaltung). */
  prefillRecipient?: boolean;
  /** Schließt z. B. die Vorschau, bevor das Mail-Fenster öffnet. */
  onBeforeOpen?: () => void;
};

type ComposeState = {
  accountId: string | null;
  leadId: string | null;
  initialTo: string;
  initialCc: string;
  initialSubject: string;
  initialBody: string;
  initialAttachments: File[];
  signatureHtml: string | null;
  fromEmail: string | null;
};

type Notice = { type: 'need_mail' } | { type: 'error'; message: string };

type ContextValue = {
  /** Bereitet die Mail vor und öffnet das Mail-Fenster; Fehler zeigt der Provider selbst an. */
  sharePdfByMail: (opts: SharePdfByMailOptions) => Promise<void>;
};

const PdfMailShareContext = React.createContext<ContextValue | null>(null);

/** `null` außerhalb des Providers (z. B. öffentliche Seiten) – dann „Per E-Mail senden“ ausblenden. */
export function usePdfMailShare(): ContextValue | null {
  return React.useContext(PdfMailShareContext);
}

async function loadLead(leadId: string): Promise<Lead | null> {
  try {
    const res = await fetch(`/api/leads/${encodeURIComponent(leadId)}`, { credentials: 'include' });
    return res.ok ? ((await res.json()) as Lead) : null;
  } catch {
    return null;
  }
}

async function loadPdfFile(opts: Pick<SharePdfByMailOptions, 'url' | 'blob' | 'filename'>): Promise<File> {
  const filename = opts.filename.trim() || 'dokument.pdf';
  if (opts.blob) return new File([opts.blob], filename, { type: opts.blob.type || 'application/pdf' });
  if (!opts.url) throw new Error('Kein PDF zum Anhängen vorhanden.');
  let bytes: Uint8Array;
  try {
    bytes = await fetchPdfBytes(opts.url);
  } catch {
    throw new Error('Das PDF konnte nicht geladen werden.');
  }
  return new File([bytes], filename, { type: 'application/pdf' });
}

/**
 * „Per E-Mail senden“ für PDF-Vorschauen und Dokument-Menüs: ein Mail-Fenster
 * für die ganze App, vorbelegt mit Empfänger, Textvorlage der Belegart und PDF-Anhang.
 */
export function PdfMailShareProvider({
  instanceId,
  children,
}: {
  instanceId: string | null;
  children: React.ReactNode;
}) {
  const [compose, setCompose] = React.useState<ComposeState | null>(null);
  const [composeKey, setComposeKey] = React.useState(0);
  const [notice, setNotice] = React.useState<Notice | null>(null);

  const sharePdfByMail = React.useCallback(async (opts: SharePdfByMailOptions) => {
    const label = opts.documentTextType ?? null;
    try {
      const dispatch = await fetchDispatchAccount(mailPurposeForDocumentTextType(label));
      if (!dispatch.account_id && !dispatch.can_choose) {
        opts.onBeforeOpen?.();
        setNotice({ type: 'need_mail' });
        return;
      }

      const leadId = opts.leadId != null && String(opts.leadId).trim() ? String(opts.leadId) : null;
      let to = (opts.to ?? '').trim();
      let cc = (opts.cc ?? '').trim();
      let recipientName = '';
      if (leadId) {
        const lead = await loadLead(leadId);
        if (lead) {
          if (!to && opts.prefillRecipient !== false) {
            // Wie „Rechnung senden“ im Dokument-Editor: Rechnungsempfänger bei Rechnungen.
            const billing = label ? resolveDocumentBilling(lead, label, null) : null;
            if (billing?.email) {
              to = billing.email;
              if (!cc) cc = (billing.cc ?? []).join(', ');
            } else {
              to = leadOutboundEmail(lead);
            }
          }
          recipientName = contactDisplayName(lead.contact ?? { name: lead.name }).trim();
        }
      }

      const greeting = recipientName ? `Sehr geehrter ${recipientName},` : 'Sehr geehrte Damen und Herren,';
      const [draft, file] = await Promise.all([
        buildDocumentMailDraft({
          documentTextType: label,
          filename: opts.filename,
          greeting,
          values: opts.templateValues,
        }),
        loadPdfFile(opts),
      ]);

      opts.onBeforeOpen?.();
      setCompose({
        accountId: dispatch.account_id,
        leadId,
        initialTo: to,
        initialCc: cc,
        initialSubject: draft.subject,
        initialBody: draft.body,
        initialAttachments: [file],
        signatureHtml: dispatch.signature_html ?? null,
        fromEmail: dispatch.email,
      });
      setComposeKey((k) => k + 1);
    } catch (err) {
      setNotice({ type: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  const value = React.useMemo(() => ({ sharePdfByMail }), [sharePdfByMail]);

  return (
    <PdfMailShareContext.Provider value={value}>
      {children}
      {compose && (
        <ComposeEmailModal
          key={`pdf-mail-${composeKey}`}
          open
          accountId={compose.accountId}
          resetKey={composeKey}
          initialTo={compose.initialTo}
          initialCc={compose.initialCc}
          initialSubject={compose.initialSubject}
          initialBody={compose.initialBody}
          initialFromEmail={compose.fromEmail}
          initialAttachments={compose.initialAttachments}
          signatureHtml={compose.signatureHtml}
          overlayClassName="z-[270]"
          leadId={compose.leadId}
          onClose={() => setCompose(null)}
          onSent={() => setCompose(null)}
        />
      )}
      {notice && (
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center bg-slate-900/45 p-4"
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget) setNotice(null);
          }}
        >
          <div className="w-full max-w-md rounded-xl bg-app-surface shadow-2xl border border-app-border overflow-hidden">
            <div className="px-5 pt-5 pb-2 text-center">
              <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-amber-100 text-amber-700">
                <AlertCircle className="h-5 w-5" />
              </div>
              <h3 className="text-sm font-semibold text-app-text">
                {notice.type === 'need_mail' ? 'Postfach erforderlich' : 'E-Mail konnte nicht vorbereitet werden'}
              </h3>
            </div>
            <div className="px-5 pb-4 text-sm text-app-text-muted leading-relaxed text-center">
              {notice.type === 'need_mail' ? (
                <p>Um Dokumente per E-Mail zu versenden, verbinden Sie bitte zuerst ein Postfach in den Einstellungen.</p>
              ) : (
                <p className="whitespace-pre-wrap">{notice.message}</p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-app-border flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setNotice(null)}
                className="px-3 py-2 rounded-lg border border-app-border text-sm text-app-text-muted hover:bg-app-hover"
              >
                Schließen
              </button>
              {notice.type === 'need_mail' && (
                <Link
                  to={`/i/${encodeURIComponent(instanceId ?? 'default')}/settings/email-dispatch`}
                  onClick={() => setNotice(null)}
                  className="inline-flex justify-center items-center px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
                >
                  E-Mail einrichten
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </PdfMailShareContext.Provider>
  );
}
